import { authStore } from "../store/authStore";
import { requestLogin } from "./authLogin";

// src/utils/api.js
const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// ─── Cookie helpers (export getCookie for App.js) ──
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// ─────────────────────────────────────────────────────────

// ─── Auto‑refresh state ──────────────────────────────
let refreshPromise = null;
let refreshSessionVersion = null;
let sessionExpiredError = null;
let pendingRequests = 0;
const requestListeners = new Set();

export const getPendingRequests = () => pendingRequests;
export const subscribeToRequests = (listener) => {
  requestListeners.add(listener);
  return () => requestListeners.delete(listener);
};

const updatePendingRequests = (change) => {
  pendingRequests += change;
  requestListeners.forEach((listener) => listener(pendingRequests));
};

// The store contains the latest token even if an older, differently scoped
// cookie with the same name is also visible to document.cookie.
const getToken = () => authStore.getAuthData().token || getCookie("auth_token") || "";

const expireSession = (error) => {
  if (!sessionExpiredError) {
    sessionExpiredError = error;
    authStore.logout();
    window.location.replace("/");
  }
};

const responseError = async (response) => {
  const text = await response.text();
  const error = new Error(`${response.status} ${text}`);
  error.status = response.status;
  try {
    error.responseData = JSON.parse(text);
  } catch {
    error.responseData = text;
  }
  return error;
};

// ─── Re‑login using stored credentials ──────────────
const reLogin = async () => {
  const version = authStore.getSessionVersion();
    const credsRaw = getCookie("auth_creds");
  if (!credsRaw) throw new Error("No stored credentials");

  let creds;
  try {
    creds = JSON.parse(credsRaw);
  } catch {
    throw new Error("Invalid stored credentials");
  }

  const result = await requestLogin(creds);
  if (authStore.getSessionVersion() !== version) {
    throw new Error("Session changed during recovery. Please try again.");
  }

  const newToken = result.access_token;
  const user = result.data;
  authStore.setuserdata(user, newToken);
  return newToken;
};

// ─── Main fetch wrapper with auto‑refresh ────────────
export const fetchWithAuth = async (url, options = {}) => {
  updatePendingRequests(1);
  try {
    if (sessionExpiredError) {
      if (!authStore.getAuthData().token) throw sessionExpiredError;
      sessionExpiredError = null;
    }

    // Requests started during recovery wait instead of sending an old token.
    if (refreshPromise && refreshSessionVersion === authStore.getSessionVersion()) {
      await refreshPromise;
    }
    const token = getToken();
    const send = (accessToken) => {
      const headers = new Headers(options.headers);
      if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
      else headers.delete("Authorization");
      if (options.body instanceof FormData) headers.delete("Content-Type");
      return fetch(url, { ...options, headers });
    };

    let response = await send(token);
    if (response.status === 401) {
      if (sessionExpiredError) throw sessionExpiredError;
      let latestToken = getToken();
      if (refreshPromise && refreshSessionVersion === authStore.getSessionVersion()) {
        latestToken = await refreshPromise;
      } else if (!latestToken || latestToken === token) {
        // A delayed 401 for an older token uses the already refreshed token.
        if (!getCookie("auth_creds")) {
          const error = await responseError(response);
          expireSession(error);
          throw error;
        }
        const recoveryVersion = authStore.getSessionVersion();
        refreshSessionVersion = recoveryVersion;
        const recovery = reLogin().catch((error) => {
          if (authStore.getSessionVersion() === recoveryVersion) expireSession(error);
          throw error;
        }).finally(() => {
          if (refreshPromise === recovery) {
            refreshPromise = null;
            refreshSessionVersion = null;
          }
        });
        refreshPromise = recovery;
        latestToken = await refreshPromise;
      }
      // Retry each request once; another 401 ends the session, not a login loop.
      response = await send(latestToken);
    }

    if (!response.ok) {
      const error = await responseError(response);
      if (response.status === 401) expireSession(error);
      throw error;
    }
    return response;
  } finally {
    updatePendingRequests(-1);
  }
};

// ─── Convenience API methods ──────────────────────────
export const api = {
  get: (path, options = {}) =>
    fetchWithAuth(API_BASE + path, { ...options, method: "GET" }),

  post: (path, body, options = {}) =>
    fetchWithAuth(API_BASE + path, {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...options.headers },
      body: JSON.stringify(body),
    }),

  put: (path, body, options = {}) =>
    fetchWithAuth(API_BASE + path, {
      ...options,
      method: "PUT",
      headers: { "Content-Type": "application/json", ...options.headers },
      body: JSON.stringify(body),
    }),

  delete: (path, options = {}) =>
    fetchWithAuth(API_BASE + path, { ...options, method: "DELETE" }),

  upload: (path, formData, options = {}) =>
    fetchWithAuth(API_BASE + path, {
      ...options,
      method: "POST",
      body: formData,
    }),
};
